export type OrganizationCustomerRow = {
  sender?: string;
  domain?: string;
  customer?: string;
  associatedEmails?: string[];
  associatedDomains?: string[];
};

function normalizeEmailPart(s: string): string {
  return s.trim().toLowerCase();
}

function findCustomerByExactSender(
  rows: OrganizationCustomerRow[],
  senderEmail: string | undefined
): OrganizationCustomerRow | undefined {
  const raw = senderEmail?.trim();

  if (!raw || !raw.includes("@") || rows.length === 0) {
    return undefined;
  }
  const normalizedSender = normalizeEmailPart(raw);

  for (const c of rows) {
    if (typeof c.sender === "string" && normalizeEmailPart(c.sender) === normalizedSender) {
      return c;
    }
  }
  return undefined;
}

function findDomainMatchedCustomerForSender(
  rows: OrganizationCustomerRow[],
  senderEmail: string | undefined
): OrganizationCustomerRow | undefined {
  const raw = senderEmail?.trim();

  if (!raw || !raw.includes("@") || rows.length === 0) {
    return undefined;
  }
  const normalizedSender = normalizeEmailPart(raw);
  const domain = normalizedSender.split("@")[1];

  if (!domain) return undefined;
  for (const c of rows) {
    if (typeof c.domain === "string" && normalizeEmailPart(c.domain) === domain) {
      return c;
    }
  }
  return undefined;
}

function findCustomerByAssociatedEmail(
  rows: OrganizationCustomerRow[],
  senderEmail: string | undefined
): OrganizationCustomerRow | undefined {
  const raw = senderEmail?.trim();

  if (!raw || !raw.includes("@") || rows.length === 0) return undefined;
  const normalizedSender = normalizeEmailPart(raw);

  for (const c of rows) {
    const hit = (c.associatedEmails ?? []).some(
      (email) => typeof email === "string" && normalizeEmailPart(email) === normalizedSender
    );

    if (hit) return c;
  }
  return undefined;
}

function findCustomerByAssociatedDomain(
  rows: OrganizationCustomerRow[],
  senderEmail: string | undefined
): OrganizationCustomerRow | undefined {
  const raw = senderEmail?.trim();

  if (!raw || !raw.includes("@") || rows.length === 0) return undefined;
  const domain = normalizeEmailPart(raw).split("@")[1];

  if (!domain) return undefined;
  for (const c of rows) {
    const hit = (c.associatedDomains ?? []).some(
      (rowDomain) =>
        typeof rowDomain === "string" && normalizeEmailPart(rowDomain).replace(/^@/, "") === domain
    );

    if (hit) return c;
  }
  return undefined;
}

/** Same resolution as server `findMatchedCustomerForSender`. */
export function findMatchedCustomerForSender(
  rows: OrganizationCustomerRow[],
  senderEmail: string | undefined
): OrganizationCustomerRow | undefined {
  return (
    findCustomerByExactSender(rows, senderEmail) ??
    findCustomerByAssociatedEmail(rows, senderEmail) ??
    findCustomerByAssociatedDomain(rows, senderEmail) ??
    findDomainMatchedCustomerForSender(rows, senderEmail)
  );
}

export function normalizeEmailPartForMatch(s: string): string {
  return normalizeEmailPart(s);
}
